/**
 * Agent Signatures Redux Slice
 *
 * Manages state for agent signatures used in retainer agreements
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AgentSignature } from 'types/signature';
import { logout } from '../auth/auth-middleware';
import {
    loadSignatures,
    loadSignature,
    createSignature,
    updateSignature,
    deleteSignature,
    setDefaultSignature,
} from './signatures.middleware';
import type { RootState } from '../store';

export interface SignaturesState {
    list: AgentSignature[];
    selectedSignature: AgentSignature | null;
    defaultSignature: AgentSignature | null;
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;
}

const initialState: SignaturesState = {
    list: [],
    selectedSignature: null,
    defaultSignature: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
};

export const signaturesSlice = createSlice({
    name: 'app/signatures',
    initialState,
    reducers: {
        /**
         * Set the selected signature
         */
        setSelectedSignature: (state, action: PayloadAction<AgentSignature | null>) => {
            state.selectedSignature = action.payload;
        },

        /**
         * Clear all signature state
         */
        resetSignatures: (state) => {
            state.list = [];
            state.selectedSignature = null;
            state.defaultSignature = null;
            state.error = null;
            state.isLoading = false;
            state.isCreating = false;
            state.isUpdating = false;
            state.isDeleting = false;
        },

        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },

        /**
         * Set error message
         */
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Load Signatures
            .addCase(loadSignatures.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadSignatures.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data) {
                    state.list = action.payload.data;
                    // Set default signature from list
                    state.defaultSignature = action.payload.data.find((s) => s.is_default) || null;
                }
            })
            .addCase(loadSignatures.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to load signatures');
            })

            // Load Single Signature
            .addCase(loadSignature.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadSignature.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data) {
                    state.selectedSignature = action.payload.data;
                    // Update in list if exists
                    const index = state.list.findIndex((s) => s.id === action.payload.data.id);
                    if (index !== -1) {
                        state.list[index] = action.payload.data;
                    }
                }
            })
            .addCase(loadSignature.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to load signature');
            })

            // Create Signature
            .addCase(createSignature.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createSignature.fulfilled, (state, action) => {
                state.isCreating = false;
                if (action.payload?.data) {
                    // Add to list
                    state.list.push(action.payload.data);
                    // If marked as default, update default signature
                    if (action.payload.data.is_default) {
                        // Unmark previous default
                        state.list.forEach((s) => {
                            if (s.id !== action.payload.data.id) {
                                s.is_default = false;
                            }
                        });
                        state.defaultSignature = action.payload.data;
                    }
                    state.selectedSignature = action.payload.data;
                }
            })
            .addCase(createSignature.rejected, (state, action) => {
                state.isCreating = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string'
                        ? errorPayload
                        : 'Failed to create signature');
            })

            // Update Signature
            .addCase(updateSignature.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateSignature.fulfilled, (state, action) => {
                state.isUpdating = false;
                if (action.payload?.data) {
                    // Update in list
                    const index = state.list.findIndex((s) => s.id === action.payload.data.id);
                    if (index !== -1) {
                        state.list[index] = action.payload.data;
                    }
                    // Update selected if matches
                    if (state.selectedSignature?.id === action.payload.data.id) {
                        state.selectedSignature = action.payload.data;
                    }
                    // Update default if matches
                    if (state.defaultSignature?.id === action.payload.data.id) {
                        state.defaultSignature = action.payload.data;
                    }
                }
            })
            .addCase(updateSignature.rejected, (state, action) => {
                state.isUpdating = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string'
                        ? errorPayload
                        : 'Failed to update signature');
            })

            // Delete Signature
            .addCase(deleteSignature.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteSignature.fulfilled, (state, action) => {
                state.isDeleting = false;
                const signatureId = action.meta.arg;
                // Remove from list
                state.list = state.list.filter((s) => s.id !== signatureId);
                // Clear selected if matches
                if (state.selectedSignature?.id === signatureId) {
                    state.selectedSignature = null;
                }
                // Clear default if matches
                if (state.defaultSignature?.id === signatureId) {
                    state.defaultSignature = null;
                }
            })
            .addCase(deleteSignature.rejected, (state, action) => {
                state.isDeleting = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string'
                        ? errorPayload
                        : 'Failed to delete signature');
            })

            // Set Default Signature
            .addCase(setDefaultSignature.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(setDefaultSignature.fulfilled, (state, action) => {
                state.isUpdating = false;
                if (action.payload?.data) {
                    const newDefaultId = action.payload.data.id;
                    // Update all signatures in list
                    state.list = state.list.map((s) => ({
                        ...s,
                        is_default: s.id === newDefaultId,
                    }));
                    // Update default signature
                    state.defaultSignature = action.payload.data;
                    // Update selected if matches
                    if (state.selectedSignature?.id === newDefaultId) {
                        state.selectedSignature = { ...state.selectedSignature, is_default: true };
                    }
                }
            })
            .addCase(setDefaultSignature.rejected, (state, action) => {
                state.isUpdating = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string'
                        ? errorPayload
                        : 'Failed to set default signature');
            })

            // Logout - clear state
            .addCase(logout.fulfilled, (state) => {
                state.list = [];
                state.selectedSignature = null;
                state.defaultSignature = null;
                state.error = null;
                state.isLoading = false;
                state.isCreating = false;
                state.isUpdating = false;
                state.isDeleting = false;
            });
    },
});

// Export actions
export const { setSelectedSignature, resetSignatures, clearError, setError } =
    signaturesSlice.actions;

// Selectors
export const selectSignatures = (state: RootState) => state.signatures.list;
export const selectDefaultSignature = (state: RootState) => state.signatures.defaultSignature;
export const selectSelectedSignature = (state: RootState) => state.signatures.selectedSignature;
export const selectSignatureById = (state: RootState, id: number) =>
    state.signatures.list.find((s) => s.id === id);
export const selectSignaturesLoading = (state: RootState) => state.signatures.isLoading;
export const selectSignaturesCreating = (state: RootState) => state.signatures.isCreating;
export const selectSignaturesUpdating = (state: RootState) => state.signatures.isUpdating;
export const selectSignaturesDeleting = (state: RootState) => state.signatures.isDeleting;
export const selectSignaturesError = (state: RootState) => state.signatures.error;
export const selectSignaturesCount = (state: RootState) => state.signatures.list.length;
export const selectCanCreateSignature = (state: RootState) => state.signatures.list.length < 10;

// Export reducer
export default signaturesSlice.reducer;
